import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import AuthWrapper from "../AuthWrapper";
import "./styles/passwordReset.css";
import { API_URLS } from "../../config"; // Import API_URLS

function PasswordReset() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [currentStep, setCurrentStep] = useState(1); // 1: email, 2: code, 3: new password
  const [resetToken, setResetToken] = useState("");
  const [isExiting, setIsExiting] = useState(false);

  // Set password-reset class on component mount
  useEffect(() => {
    const authLeft = document.querySelector('.auth-left');
    if (authLeft) {
      authLeft.classList.remove('register');
      authLeft.classList.add('password-reset');
    }
  }, []);

  useEffect(() => {
    /* Body, html overflow: hidden !improtant; */
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Timer logic
  useEffect(() => {
    let timer;
    if (currentStep === 2 && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && currentStep === 2) {
      setCurrentStep(1);
      setMessage("");
    }
    return () => clearInterval(timer);
  }, [currentStep, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await fetch(API_URLS.passwordResetRequest, { // Use config
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      
      if (response.ok) {
        setMessage("Reset code sent! Please check your email.");
        setCurrentStep(2);
        setTimeLeft(300); // Reset timer to 5 minutes
      } else {
        setError(data.message || "Failed to send reset code.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    }
  };

  const handleCodeVerification = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await fetch(API_URLS.verifyResetCode, { // Use config
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: resetCode }),
      });
      const data = await response.json();
      
      if (response.ok) {
        setResetToken(data.resetToken);
        setMessage("Code verified successfully!");
        setCurrentStep(3);
      } else {
        setError(data.message || "Invalid code.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    try {
      const response = await fetch(API_URLS.resetPassword, { // Use config
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resetToken, newPassword }),
      });
      const data = await response.json();
      
      if (response.ok) {
        setMessage("Password reset successful!");
        setTimeout(() => navigateWithTransition('/login'), 2000);
      } else {
        setError(data.message || "Failed to reset password.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    }
  };

  const navigateWithTransition = (path) => {
    setIsExiting(true);
    const form = document.querySelector('.form');
    form.classList.remove('entered');
    form.classList.add('exiting');
    
    const authLeft = document.querySelector('.auth-left');
    if (path === '/sign-up') {
      authLeft.classList.add('register');
      authLeft.classList.remove('password-reset');
    } else {
      authLeft.classList.remove('register', 'password-reset');
    }
    
    setTimeout(() => {
      navigate(path);
    }, 650);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="password-reset-step-container active">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="login-button">
              Send Reset Code
            </button>
          </div>
        );
      
      case 2:
        return (
          <div className="password-reset-step-container active">
            <div className="timer">
              Code expires in: {formatTime(timeLeft)}
            </div>
            <div className="form-group">
              <label htmlFor="resetCode">Enter Reset Code</label>
              <input
                id="resetCode"
                type="text"
                placeholder="Enter 6-digit code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                maxLength="6"
                required
              />
            </div>
            <button type="submit" className="login-button">
              Verify Code
            </button>
            <button
              type="button"
              className="navigation-button"
              onClick={() => {
                setCurrentStep(1);
                setTimeLeft(300);
              }}
            >
              Request New Code
            </button>
          </div>
        );
      
      case 3:
        return (
          <div className="password-reset-step-container active">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="login-button">
              Reset Password
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    switch (currentStep) {
      case 1:
        handleEmailSubmit(e);
        break;
      case 2:
        handleCodeVerification(e);
        break;
      case 3:
        handlePasswordReset(e);
        break;
      default:
        break;
    }
  };

  return (
    <AuthWrapper>
      <CSSTransition 
        in={!isExiting} 
        appear={true} 
        timeout={650} 
        classNames="page-transition"
      >
        <div className="glass-box-auth">
          <h1 className="title">Password Reset</h1>
          <p className="subtitle">
            {currentStep === 1 && "Enter your email to reset your password"}
            {currentStep === 2 && "Enter the 6-digit code sent to your email"}
            {currentStep === 3 && "Enter your new password"}
          </p>

          <form className="form entered" onSubmit={handleSubmit}>
            {renderStep()}
          </form>

          <div className="transition-links">
            <p className="register">
              Remember your password?{' '}
              <a
                onClick={(e) => {
                  e.preventDefault();
                  navigateWithTransition('/login');
                }}
                href="/login"
                className="register-link"
              >
                Login
              </a>
            </p>
            <p className="register">
              Don't have an account?{' '}
              <a
                onClick={(e) => {
                  e.preventDefault();
                  navigateWithTransition('/sign-up');
                }}
                href="/sign-up"
                className="register-link"
              >
                Register
              </a>
            </p>
          </div>
          {message && <p className="success-message">{message}</p>}
          {error && <p className="error">{error}</p>}
        </div>
      </CSSTransition>
    </AuthWrapper>
  );
}

export default PasswordReset;
